import React, { useEffect, useState } from 'react';
import illumass from '../../services/illumass';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogContent } from '@material-ui/core';
import DialogTitle from '../dialog-title';
import MaterialTable from 'material-table';
import { SearchResult, Tenant } from '@equestria/spike';
import _ from 'lodash';
import { RarityAppState } from '../../store';
import { useSnackbar } from 'notistack';

interface SwitchTenantDialogProps {
  open: boolean;
  onClose: (switchedTenant: boolean) => void;
}

export default function SwitchTenantDialog(
  props: SwitchTenantDialogProps
): JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const { currentTenantId } = useSelector<
    RarityAppState,
    { currentTenantId: string }
  >(
    (state) => {
      return {
        currentTenantId: _.get(state, ['tenant', 'id'], ''),
      };
    },
    () => true // We only want to load this value once
  );

  useEffect(() => {
    const processSearchResult = async (
      p: Promise<SearchResult<Tenant> | null>,
      collectedTenants: Tenant[] = []
    ): Promise<Tenant[]> => {
      const result = await p;

      if (result !== null) {
        const nextTenants = _.concat(
          collectedTenants,
          _.map(result.hits, (hit) => hit._source)
        );
        if (result.fetched === result.total) {
          return nextTenants;
        } else {
          return processSearchResult(result.next(), nextTenants);
        }
      } else {
        return collectedTenants;
      }
    };

    illumass.kuzzle.volatile = { rumble: { tenantId: '*' } };
    processSearchResult(
      illumass.tenant.search({}, { scroll: '30s', size: 100 })
    )
      .then((searchedTenants) => {
        setTenants(_.sortBy(searchedTenants, (t) => t.name));
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        enqueueSnackbar('Unable to load tenants.', { variant: 'error' });
        setLoading(false);
      });

    if (currentTenantId !== '') {
      illumass.kuzzle.volatile = { rumble: { tenantId: currentTenantId } };
    } else {
      _.unset(illumass.kuzzle.volatile, ['rumble']);
    }
  }, [currentTenantId, enqueueSnackbar]);

  const handleRowClick = (
    _event: React.MouseEvent<Element, MouseEvent> | undefined,
    rowData: Tenant | undefined
  ): void => {
    if (rowData !== undefined && rowData.id !== currentTenantId) {
      dispatch({
        type: 'switchTenant',
        tenant: {
          id: rowData.id ?? '',
          shortName: rowData.name ?? '',
        },
      });
      props.onClose(true);
    } else {
      props.onClose(false);
    }
  };

  return (
    <Dialog
      open={props.open}
      onClose={(): void => props.onClose(false)}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle
        id="switch-customer-title"
        onClose={(): void => props.onClose(false)}
      >
        Switch customer
      </DialogTitle>
      <DialogContent dividers>
        <MaterialTable
          isLoading={loading}
          columns={[
            {
              title: 'Customer',
              field: 'name',
              searchable: true,
              sorting: true,
            },
          ]}
          data={tenants}
          options={{
            sorting: true,
            header: false,
            search: true,
            searchFieldAlignment: 'left',
            showTitle: false,
          }}
          onRowClick={handleRowClick}
        />
      </DialogContent>
    </Dialog>
  );
}
